import { useTexte } from '../../texte'
import { createPortal } from 'react-dom'

import type { Poll } from '../../api/types'
import { XIcon } from '../../components/Icons'
import { useEscape } from '../../hooks/useEscape'

/**
 * Das Ergebnis im Detail — wer wie abgestimmt hat (#56).
 *
 * **Es steht ausschließlich dem Autor offen, und das entscheidet nicht
 * diese Datei.** `poll.votes` kommt bei einem Mitlesenden gar nicht erst an;
 * dieses Blatt lässt sich also nicht dadurch öffnen, dass jemand einen Knopf
 * findet. Genau so herum ist es richtig: eine Oberfläche, die eine Liste
 * bekommt und sie „nicht anzeigt", hat sie trotzdem ausgeliefert.
 *
 * **Am Seitenkörper und in der Hausform** (`createPortal`, `.bogen`). Die
 * Blätter dieser Anwendung tragen seit dem milchigen Material ein
 * `backdrop-filter`, und das macht ein Element zum Bezugsrahmen für
 * `position: fixed` — ein Blatt, das im Verlauf steckt, klebt am Verlauf und
 * scrollt mit. Steht in `CLAUDE.md`, und es ist zweimal passiert.
 */
export function Abstimmungsblatt({ poll, onZu }: { poll: Poll; onZu: () => void }) {
  // Escape sagt Nein -- wie an jedem Blatt hier.
  const t = useTexte()
  useEscape(onZu)

  const namenZu = new Map(poll.options.map((o) => [o.id, o.label]))
  const abstimmende = poll.voters ?? 0

  return createPortal(
    <div className="bogen bogen--ergebnis" role="dialog" aria-modal="true" aria-label={t.chat.ergebnis}>
      <div className="bogen__blatt bogen__blatt--gegliedert">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{poll.question}</h3>
          <button type="button" className="icon-button" onClick={onZu} aria-label={t.allgemein.schliessen}>
            <XIcon size={20} />
          </button>
        </div>

        {/* **Kopf und Inhalt als Geschwister** (1.61.0). No foot here -- what
            this sheet gains is the standing head: with many voters the list
            scrolls, and the question itself used to scroll away with it. */}
        <div className="bogen__rolle">
          <p className="bogen__zeile">
            {t.chat.stimmenZeile(abstimmende, poll.closed_at === null)}
          </p>

          {/* Zuerst das Ganze: Antwort, Zahl, Anteil. */}
          <ul className="ergebnis__zahlen">
            {poll.options.map((option) => (
              <li key={option.id}>
                <span className="ergebnis__antwort">
                  {/* The right answer (#143), as on the card. */}
                  {poll.closed_at !== null && option.correct === true && (
                    <span className="abstimmung__sign abstimmung__sign--right" aria-hidden="true">✓ </span>
                  )}
                  {option.label}
                  {poll.closed_at !== null && option.correct === true && (
                    <span className="visually-hidden">{t.chat.pollRightSr}</span>
                  )}
                </span>
                <span className="ergebnis__zahl">
                  {option.count ?? 0} · {option.percent ?? 0} %
                </span>
              </li>
            ))}
          </ul>

          <h4 className="ergebnis__ueberschrift">{t.chat.werWie}</h4>
          {(poll.votes ?? []).length === 0 ? (
            <p className="feld__hinweis">{t.chat.niemandAbgestimmt}</p>
          ) : (
            <ul className="ergebnis__leute">
              {(poll.votes ?? []).map((stimme) => (
                <li key={stimme.user.id}>
                  <span className="ergebnis__name">{stimme.user.display_name}</span>
                  <span className="ergebnis__wahl">
                    {stimme.option_ids.map((id) => namenZu.get(id) ?? '—').join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* **Wer fehlt, steht nicht da — und das ist eine Entscheidung.** Eine
              Liste „hat noch nicht abgestimmt" wäre bequem und machte aus einer
              Abstimmung eine Anwesenheitskontrolle. Wer sie braucht, findet die
              Empfänger in den Reiseangaben. */}
        </div>
      </div>
    </div>,
    document.body,
  )
}
